import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, User, Mail, Lock, Phone, CreditCard, ArrowLeft, AlertCircle } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { getApiBaseUrl } from '@/config/production';
import { SessionManager } from '@/utils/sessionManager';

export default function RegisterPage() {
  const [location, setLocation] = useLocation();
  const { login } = useUser();
  
  // Verificar query string para pré-selecionar tipo de usuário
  const getInitialUserType = (): 'Athlete' | 'Photographer' => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'Athlete' || type === 'Photographer') {
      return type;
    }
    return 'Athlete';
  };

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    cpf: '',
    phone: '',
    userType: getInitialUserType()
  });

  // Atualizar userType quando a query string mudar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'Athlete' || type === 'Photographer') {
      setFormData(prev => ({ ...prev, userType: type }));
    }
  }, [location]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Garantir que não há barra dupla na construção da URL
  const getApiBase = () => {
    const base = getApiBaseUrl();
    return base.replace(/\/+$/, ''); // Remove barras finais
  };
  const API_BASE = getApiBase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validações
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
          cpf: formData.cpf,
          phone: formData.phone,
          userType: formData.userType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao criar conta');
      }

      setSuccess('Conta criada com sucesso! Você pode fazer login agora.');
      
      // Se o registro foi bem-sucedido e há uma sessão, fazer login automático
      if (data.data?.session) {
        SessionManager.saveTokensDirectly(data.session.access_token, data.session.refresh_token);
        setLocation('/');
      } else {
        // Se não há sessão, redirecionar para login
        setTimeout(() => {
          setLocation('/login');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Erro no registro:', error);
      setError(error.message || 'Erro ao criar conta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf: formatted
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  return (
    <div 
      className="min-h-screen flex relative"
      style={{
        backgroundImage: 'url(/Fotup_Wallpaper.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay suave */}
      <div className="absolute inset-0 bg-black/40"></div>
      
      {/* Lado esquerdo - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col justify-center items-center text-white p-12">
        <div className="text-center max-w-md">
          {/* Logo com animação */}
          <div className="flex items-center justify-center mb-8 animate-fade-in-up">
            <div className="relative group">
              <img 
                src="/Logo_2.png" 
                alt="FotUP Logo" 
                className="h-20 w-auto mr-4 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
              />
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-5xl font-bold leading-tight bg-gradient-to-r from-white via-white to-primary/80 bg-clip-text text-transparent animate-pulse-slow">
                FotUP
              </span>
              <span className="text-lg text-white/80 leading-tight animate-fade-in-delay">
                Fotografia Esportiva
              </span>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent animate-fade-in-up-delay">
            Bem-vindo ao FotUp!
          </h2>
          <p className="text-xl mb-8 opacity-90 animate-fade-in-up-delay">
            A plataforma que conecta atletas e fotógrafos de eventos esportivos
          </p>
          
          <div className="space-y-6 text-lg">
            <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer group animate-fade-in-up-delay-1">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 group-hover:scale-110 transition-all duration-300">
                <span className="text-lg font-bold group-hover:animate-bounce">1</span>
              </div>
              <span className="text-white/90 group-hover:text-white transition-colors duration-300">Encontre suas fotos por número de peito</span>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer group animate-fade-in-up-delay-2">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 group-hover:scale-110 transition-all duration-300">
                <span className="text-lg font-bold group-hover:animate-bounce">2</span>
              </div>
              <span className="text-white/90 group-hover:text-white transition-colors duration-300">Compre ou baixe gratuitamente</span>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer group animate-fade-in-up-delay-3">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 group-hover:scale-110 transition-all duration-300">
                <span className="text-lg font-bold group-hover:animate-bounce">3</span>
              </div>
              <span className="text-white/90 group-hover:text-white transition-colors duration-300">Compartilhe seus melhores momentos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lado direito - Formulário */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-2xl">
          {/* Header compacto */}
          <div className="mb-4 animate-fade-in-up">
            <Link href="/" className="inline-flex items-center text-sm text-white hover:text-white/80 mb-3 transition-colors duration-300 group">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
              Voltar ao início
            </Link>
            
            <div className="text-center mb-4">
              <div className="flex items-center justify-center mb-2">
                <div className="p-2 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-lg">
                  <img 
                    src="/Logo_2.png" 
                    alt="FotUP Logo" 
                    className="h-8 w-auto"
                  />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-1">Criar conta</h1>
              <p className="text-white/80 text-sm">
                Junte-se à comunidade FotUp
              </p>
            </div>
          </div>

          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm animate-slide-in-right hover:shadow-3xl transition-all duration-500">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent animate-fade-in-up">
                Preencha seus dados
              </CardTitle>
              <CardDescription className="text-sm text-gray-600 animate-fade-in-up-delay">
                Escolha seu tipo de conta e complete o cadastro
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="border-green-200 bg-green-50 text-green-800 mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{success}</AlertDescription>
                  </Alert>
                )}

                {/* Layout em duas colunas para otimizar espaço */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Coluna esquerda - Tipo de conta e dados básicos */}
                  <div className="space-y-4">
                    {/* Tipo de usuário - Compacto */}
                    <div className="space-y-3 animate-fade-in-up-delay-1">
                      <Label className="text-sm font-medium text-gray-700">Tipo de conta</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Atleta */}
                        <div 
                          className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 group ${
                            formData.userType === 'Athlete' 
                              ? 'border-primary bg-primary/5 shadow-md scale-105' 
                              : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, userType: 'Athlete' }))}
                        >
                          <div className="text-center">
                            <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                              formData.userType === 'Athlete' 
                                ? 'bg-primary text-white' 
                                : 'bg-gray-100 text-gray-600 group-hover:bg-primary/10 group-hover:text-primary'
                            }`}>
                              <span className="text-lg">🏃</span>
                            </div>
                            <h3 className={`text-sm font-semibold transition-colors duration-300 ${
                              formData.userType === 'Athlete' ? 'text-primary' : 'text-gray-700'
                            }`}>
                              Atleta
                            </h3>
                          </div>
                          {formData.userType === 'Athlete' && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                          )}
                        </div>

                        {/* Fotógrafo */}
                        <div 
                          className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 group ${
                            formData.userType === 'Photographer' 
                              ? 'border-primary bg-primary/5 shadow-md scale-105' 
                              : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, userType: 'Photographer' }))}
                        >
                          <div className="text-center">
                            <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                              formData.userType === 'Photographer' 
                                ? 'bg-primary text-white' 
                                : 'bg-gray-100 text-gray-600 group-hover:bg-primary/10 group-hover:text-primary'
                            }`}>
                              <span className="text-lg">📸</span>
                            </div>
                            <h3 className={`text-sm font-semibold transition-colors duration-300 ${
                              formData.userType === 'Photographer' ? 'text-primary' : 'text-gray-700'
                            }`}>
                              Fotógrafo
                            </h3>
                          </div>
                          {formData.userType === 'Photographer' && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {formData.userType === 'Photographer' && (
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in-up">
                          <div className="flex items-start gap-2">
                            <span className="text-amber-600 text-xs">⚠️</span>
                            <p className="text-xs text-amber-700">
                              Contas de fotógrafos precisam de aprovação.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Nome completo */}
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">Nome completo</Label>
                      <div className="relative group">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className="pl-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="Seu nome completo"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                      <div className="relative group">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="pl-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="seu@email.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Coluna direita - Dados adicionais e senhas */}
                  <div className="space-y-4">
                    {/* CPF */}
                    <div className="space-y-2">
                      <Label htmlFor="cpf" className="text-sm font-medium text-gray-700">CPF</Label>
                      <div className="relative group">
                        <CreditCard className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="cpf"
                          name="cpf"
                          type="text"
                          value={formData.cpf}
                          onChange={handleCPFChange}
                          className="pl-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="000.000.000-00"
                          maxLength={14}
                        />
                      </div>
                    </div>

                    {/* Telefone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Telefone</Label>
                      <div className="relative group">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="phone"
                          name="phone"
                          type="text"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          className="pl-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="(11) 99999-9999"
                          maxLength={15}
                        />
                      </div>
                    </div>

                    {/* Senha */}
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium text-gray-700">Senha</Label>
                      <div className="relative group">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleInputChange}
                          className="pl-10 pr-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="Mínimo 6 caracteres"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-primary transition-colors duration-300"
                        >
                          {showPassword ? <EyeOff /> : <Eye />}
                        </button>
                      </div>
                    </div>

                    {/* Confirmar senha */}
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">Confirmar senha</Label>
                      <div className="relative group">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors duration-300" />
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="pl-10 pr-10 h-10 transition-all duration-300 focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50"
                          placeholder="Digite a senha novamente"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-primary transition-colors duration-300"
                        >
                          {showConfirmPassword ? <EyeOff /> : <Eye />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botão de submit */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-medium bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 animate-fade-in-up-delay-3 relative overflow-hidden group"
                    disabled={isLoading}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Criando conta...
                        </>
                      ) : (
                        <>
                          Criar conta
                          <ArrowLeft className="ml-2 h-4 w-4 rotate-180 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Button>
                </div>
              </form>

              <div className="mt-4 text-center animate-fade-in-up-delay-3">
                <p className="text-sm text-gray-600">
                  Já tem uma conta?{' '}
                  <Link href="/login" className="text-primary hover:text-primary/80 font-medium transition-colors duration-300 hover:underline">
                    Fazer login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}
